import { LSelect } from "./ui/LSelect";
import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { loyaltyService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { Search, Wallet, X, Loader2, Plus } from "lucide-react";

const BAL_TYPES = ["points", "cashback", "voucher", "reward"];
const TXN_TYPES = ["earn", "redeem", "adjust", "expire", "cashback"];

export default function CustomerWalletPage() {
  const { hasPermission } = useAuth();
  const { format } = useCurrency();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [adjust, setAdjust] = useState(null); // wallet being adjusted
  const [form, setForm] = useState({ balanceType: "points", type: "earn", amount: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const canAdjust = hasPermission("LOYALTY.UPDATE");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await loyaltyService.wallets({ q: q || undefined })).items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await loyaltyService.createTransaction(adjust.customerId, {
        balanceType: form.balanceType, type: form.type, amount: Number(form.amount), reason: form.reason || undefined,
      });
      toast.success("Transaction recorded");
      setAdjust(null); setForm({ balanceType: "points", type: "earn", amount: "", reason: "" }); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const columns = [
    { key: "customerId", header: "Customer", render: (w) => <span className="font-mono text-sm text-foreground">{w.customerId}</span> },
    { key: "pointsBalance", header: "Points", render: (w) => <span className="font-semibold">{(w.pointsBalance || 0).toLocaleString()}</span> },
    { key: "cashbackBalance", header: "Cashback", render: (w) => format(w.cashbackBalance || 0) },
    { key: "voucherBalance", header: "Vouchers", render: (w) => (w.voucherBalance || 0).toLocaleString() },
    { key: "tier", header: "Tier", render: (w) => w.tierName || "—" },
    { key: "lifetime", header: "Lifetime Earned", render: (w) => (w.lifetimePointsEarned || 0).toLocaleString() },
    { key: "act", header: "", render: (w) => canAdjust && (
      <button onClick={() => setAdjust(w)} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`wallet-adjust-${w.customerId}`}>
        <Plus className="h-3.5 w-3.5" /> Transaction
      </button>
    ) },
  ];

  return (
    <div data-testid="customer-wallet-page">
      <PageHeader title="Customer Wallet" subtitle="Points, cashback, voucher & reward balances per customer" />

      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by customer ID…" className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm" data-testid="wallet-search-input" />
      </div>

      <DataTable testid="wallets-table" rows={rows} loading={loading} columns={columns} empty="No customer wallets yet." />

      {adjust && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="wallet-adjust-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setAdjust(null)} />
          <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /><h3 className="font-semibold text-foreground">New Transaction</h3></div>
              <button onClick={() => setAdjust(null)}><X className="h-5 w-5 text-muted-foreground hover:text-foreground" /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-muted-foreground">Customer <span className="font-mono text-foreground">{adjust.customerId}</span></p>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-foreground mb-1">Balance</label>
                  <LSelect value={form.balanceType} onChange={(e) => setForm({ ...form, balanceType: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="adjust-balance-select">
                    {BAL_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </LSelect></div>
                <div><label className="block text-sm font-medium text-foreground mb-1">Type</label>
                  <LSelect value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="adjust-type-select">
                    {TXN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </LSelect></div>
              </div>
              <div><label className="block text-sm font-medium text-foreground mb-1">Amount</label>
                <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="adjust-amount-input" placeholder="e.g. 250" /></div>
              <div><label className="block text-sm font-medium text-foreground mb-1">Reason</label>
                <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" placeholder="e.g. Purchase reward" /></div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
              <button onClick={() => setAdjust(null)} className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted">Cancel</button>
              <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="adjust-save-button">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
